import {
  Box,
  BoxProps,
  Button,
  Divider,
  IconButton,
  Modal,
  Stack,
  StackProps,
  Typography,
  TypographyProps,
  ButtonProps,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

interface BasicModalProps {
  id?: string
  open: boolean
  onClose?: () => void
  overlayStyle?: React.CSSProperties
  sx?: BoxProps['sx']
  title?: string
  subTitle?: string
  showCloseButton?: boolean
  slotProps?: {
    header?: StackProps
    title?: TypographyProps
    subTitle?: TypographyProps
  }
  footerActions?: Array<{
    title?: ButtonProps['children']
    onClick?: ButtonProps['onClick']
    variant?: ButtonProps['variant']
    color?: ButtonProps['color']
    disabled?: ButtonProps['disabled']
  }>
}

const BasicModal: React.FC<React.PropsWithChildren<BasicModalProps>> = ({
  id,
  open,
  children,
  onClose,
  sx,
  title,
  subTitle,
  showCloseButton,
  slotProps,
  footerActions,
}) => {
  return (
    <Modal
      id={id}
      open={open}
      onClose={onClose}
      sx={{ outline: 0 }}
      className="basic-modal"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '880px',
          maxWidth: '95vw',
          maxHeight: '80vh',
          bgcolor: 'background.paper',
          transform: 'translate(-50%, -50%)',
          borderRadius: '8px',
          overflow: 'hidden',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          ...sx,
        }}
      >
        {title || showCloseButton ? (
          <Stack
            direction="row"
            justifyContent={title ? 'space-between' : 'flex-end'}
            alignItems="center"
            {...slotProps?.header}
          >
            <Stack>
              <Typography
                variant="subtitle2"
                component="h3"
                {...slotProps?.title}
                sx={{ fontWeight: 700, ...slotProps?.title?.sx }}
              >
                {title}
              </Typography>
              {subTitle && (
                <Typography
                  variant="body1"
                  component="h3"
                  {...slotProps?.subTitle}
                  sx={{ fontWeight: 400, ...slotProps?.subTitle?.sx }}
                >
                  {subTitle}
                </Typography>
              )}
            </Stack>

            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        ) : null}

        {title || showCloseButton ? <Divider sx={{ my: 1 }} /> : null}

        {/* Scrollable content area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            scrollbarWidth: 'thin' /* Firefox */,
            scrollbarColor: '#c1c9d2 #f1f3f5' /* thumb track */,
            '&::-webkit-scrollbar': {
              width: 10,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f3f5',
              borderRadius: 8,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c9d2',
              borderRadius: 8,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#a8b2bd',
            },
          }}
        >
          {children}
        </Box>

        {footerActions?.length ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" justifyContent="flex-end" gap={2}>
              {footerActions.map((action, index) => (
                <Button
                  key={index + String(title)}
                  variant={action.variant}
                  color={action.color}
                  onClick={action.onClick}
                  disabled={action.disabled}
                >
                  {action.title}
                </Button>
              ))}
            </Stack>
          </>
        ) : null}
      </Box>
    </Modal>
  )
}

export default BasicModal
